import LoginForm from "@/components/login-form"
import MobileLoginForm from "@/components/mobile-login-form";
import MobileLandscapeLogin from "@/components/mobile-landscape-login";
import { WithTranslation, withTranslation } from 'react-i18next';
import { useNavigate } from "react-router";
import { ROUTE } from "@/routes/constants";
import Welcome from "@/components/welcome";
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { Theme, THEME, STAGE_W, STAGE_H } from "@/components/login-theme";
import { useIsMobileLandscapeStage } from "@/hooks/use-mobile";
import { CompetitionRulesDialog } from "@/components/competition-rules-dialog";
import ThemeSelector from "@/components/theme-selector";

// 响应式:用 Tailwind `lg`(1024px) 切布局——<1024 移动流式,≥1024 为 1920×1080 等比缩放画布。

// 语言切换:胶囊轨道+白滑块(设计稿 139×39);variant='mobile' 去绝对定位、随顶栏流式排布。
function LangToggle({ chip, variant = 'desktop' }: { chip: string; variant?: 'desktop' | 'mobile' }) {
  const { i18n } = useTranslation();
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');
  // LanguageDetector 已停用,changeLanguage 不写 cookie;各页优先读 cookie,故须手动同步,否则旧值覆盖切换(对齐 language-box.tsx)。
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  const seg = (active: boolean): React.CSSProperties => ({
    height: 31,
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 28.5,
    background: active ? '#FFFFFF' : 'transparent',
    color: '#000',
    fontSize: 14,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
    border: 0,
  });

  // 1920 设计稿：胶囊容器 139×39（padding 4 + gap 4 + 两个 segment 自适应）
  const container: React.CSSProperties = variant === 'mobile'
    ? { height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip }
    : { position: 'absolute', left: 1725, top: 28, width: 139, height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip };

  return (
    <div style={container}>
      <button type="button" onClick={() => setLang('en')} style={seg(isEn)}>English</button>
      <button type="button" onClick={() => setLang('zh-HK')} style={seg(!isEn)}>繁體</button>
    </div>
  );
}

function LoginPage({ t }: WithTranslation) {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>('orange');
  const colors = THEME[theme];
  // ZH-13 开放时段提示暂不展示（2026-07-28 甲方要求），恢复时把下面两行连同两处 <p> 一起放回：
  // const startTime = import.meta.env.VITE_ALLOWED_START_TIME || '6:00';
  // const endTime = import.meta.env.VITE_ALLOWED_END_TIME || '23:00';
  // 手机横屏舞台判定：用设备物理朝向(screen.orientation),不受软键盘压缩视口影响。见 useIsMobileLandscapeStage。
  const isLandscapeStage = useIsMobileLandscapeStage();

  // 将 1920×1080 画布缩放至视口(仅 ≥lg)。视口比 16:9 宽时按高度缩放、横向余量灌入中部空白
  // (卡片贴左、表单贴右,元素尺寸/纵向位置不变,消两侧留白);更窄/高时维持等比居中(可能上下留白)。
  const [{ scale, shift }, setLayout] = useState({ scale: 1, shift: 0 });
  useEffect(() => {
    const update = () => {
      const sW = window.innerWidth / STAGE_W;
      const sH = window.innerHeight / STAGE_H;
      if (sW >= sH) {
        setLayout({ scale: sH, shift: window.innerWidth / sH - STAGE_W });
      } else {
        setLayout({ scale: sW, shift: 0 });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const pillStyle = (bg: string): React.CSSProperties => ({
    position: 'absolute',
    top: 28,
    width: 90,
    height: 39,
    borderRadius: 28.5,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 400,
    color: '#000',
    border: 0,
    cursor: 'pointer',
  });

  return (
    <>
      {/* 移动端·竖屏：顶部语言切换 / 中间登录表单 / 底部两个机构 logo（垂直流式，可滚动）。lg:hidden 保证 ≥lg 走桌面画布。
          手机横屏另有独立 844×390 舞台,故 isLandscapeStage 为真时本竖屏分支 :hidden。
          ⚠️ isLandscapeStage 以设备物理朝向(screen.orientation)判定,而非 CSS (orientation:landscape)/(max-height:500px)：
          真机(微信 X5 等)竖屏弹软键盘会压缩视口令 CSS 误判横屏→本表单被卸载→键盘弹不起。物理朝向不受键盘影响,详见 useIsMobileLandscapeStage。 */}
      <div className={`lg:hidden fixed inset-0 overflow-y-auto ${isLandscapeStage ? "hidden" : "flex flex-col"}`} style={{ backgroundColor: colors.pageBg }}>
        {/* 顶部：仅语言切换，右对齐 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px' }}>
          <LangToggle chip={colors.chip} variant="mobile" />
        </div>

        {/* 中间：登录表单（垂直居中，限制最大宽度） */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '8px 24px 24px' }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            <MobileLoginForm colors={colors} />
          </div>
          {/* ZH-13 开放时段提示：按甲方要求暂不展示（2026-07-28） */}
        </div>

        {/* 底部：UGC + 教大 logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, padding: '20px 16px 12px' }}>
          <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 28, width: 'auto' }} />
          <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 28, width: 'auto' }} />
        </div>

        {/* 底部版权小字 */}
        <div style={{ textAlign: 'center', padding: '0 16px 24px', fontSize: 12, color: '#D1D5DB' }}>
          {t('login.copyright')}
        </div>
      </div>

      {/* 手机横屏(设备物理横屏 且 <lg)：独立 844×390 设计稿舞台(FitScale 居中)。
          lg:hidden! 确保 ≥lg(即便矮屏)仍走桌面画布,与竖屏移动流/桌面画布三分支互斥。
          isLandscapeStage 见上:以物理朝向判定,防止竖屏软键盘压缩视口误触本横屏舞台。 */}
      <div className={`lg:hidden! fixed inset-0 items-center justify-center overflow-hidden ${isLandscapeStage ? "flex" : "hidden"}`} style={{ backgroundColor: colors.pageBg }}>
        <MobileLandscapeLogin theme={theme} setTheme={setTheme} colors={colors} />
      </div>

      {/* 桌面端(≥lg)：1920×1080 缩放画布；shift 为超宽屏下灌入中部的额外横向宽度 */}
      <div className="hidden lg:flex fixed inset-0 items-center justify-center overflow-hidden" style={{ backgroundColor: colors.pageBg }}>
        <div style={{ width: STAGE_W + shift, height: STAGE_H, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', flex: '0 0 auto' }}>
        {/* 左侧卡片（贴左，坐标不变） */}
        <Welcome theme={theme} colors={colors} />

        {/* 右侧元素整体右移 shift（贴右）：内部仍沿用设计稿 1920 坐标，仅由本容器整体偏移 */}
        <div style={{ position: 'absolute', left: shift, top: 0, width: STAGE_W, height: STAGE_H }}>
          {/* 顶部胶囊：關於我們 / 活動介紹 （frame x=1041 / 1149） */}
          <button type="button" onClick={() => navigate(ROUTE.ABOUT)} style={{ ...pillStyle(colors.pillAbout), left: 1041 }} className="hover:brightness-[0.97]">
            {t("login.about-us")}
          </button>
          <CompetitionRulesDialog>
            <button type="button" style={{ ...pillStyle(colors.pillActivity), left: 1149 }} className="hover:brightness-[0.97]">
              {t("login.activities")}
            </button>
          </CompetitionRulesDialog>

          {/* 主题切换器 + 语言切换 */}
          <div style={{ position: 'absolute', left: 1505, top: 28 }}>
            <ThemeSelector theme={theme} onChange={setTheme} />
          </div>
          <LangToggle chip={colors.chip} />

          {/* 右侧表单 */}
          <LoginForm theme={theme} colors={colors} />

          {/* ZH-13 平台开放时段提示：按甲方要求暂不展示(2026-07-28)；文案键 login.openingHours 与 startTime/endTime 读取保留，需要时把 <p> 放回 */}

          {/* 右下角机构 Logo + 版权小字：logo 行在上，版权居中于其正下方
              （frame UGC@(1513,1019) 204×33，教大@(1758,1019) 106×33） */}
          <div style={{ position: 'absolute', left: 1513, top: 1011, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 41 }}>
              <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 33, width: 'auto' }} />
              <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 33, width: 'auto' }} />
            </div>
            <div style={{ fontSize: 13, color: '#D1D5DB', whiteSpace: 'nowrap' }}>
              {t('login.copyright')}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default withTranslation()(LoginPage);
